// Admin Settings Constants

import type {BenchmarkMetricKey} from '@/services/program/programService'

// ===================== Toast Messages =====================
export const TOAST_MESSAGES = {
  // Dashboard Management
  DASHBOARD_REMOVED: 'Dashboard Removed',
  DASHBOARD_REMOVED_SUCCESS: (name: string) => `${name} has been successfully removed.`,
  DASHBOARD_REMOVE_ERROR: 'Failed to remove dashboard. Please try again.',

  // Program Management
  PROGRAM_ENABLED: 'Program Enabled',
  PROGRAM_ENABLED_SUCCESS: (name: string) => `${name} has been enabled on SE Metrics page.`,
  PROGRAM_DISABLED: 'Program Disabled',
  PROGRAM_DISABLED_SUCCESS: (name: string) => `${name} has been disabled from SE Metrics page.`,
  PROGRAM_ENABLE_ERROR: 'Failed to enable program. Please try again.',
  PROGRAM_DISABLE_ERROR: 'Failed to disable program. Please try again.',
  PROGRAM_DATA_CHECK_ERROR: 'Failed to check program data. Please try again.',

  // Global SE Metrics KPI
  GLOBAL_KPI_SUCCESS_TITLE: 'Success',
  GLOBAL_KPI_UPDATE_SUCCESS: 'The global KPIs has been successfully updated.',
  GLOBAL_KPI_UPDATE_ERROR: 'Failed to update global KPIs. Please try again.',

  // Generic
  ERROR: 'Error',
  UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
  LOAD_ERROR: 'Failed to load programs. Please try again.',
} as const

// ===================== Modal Messages =====================
export const MODAL_MESSAGES = {
  // Dashboard Deletion
  DELETE_DASHBOARD_TITLE: 'Remove Dashboard',
  DELETE_DASHBOARD_CONFIRM_PREFIX: 'Are you sure you want to remove the',
  DELETE_DASHBOARD_CONFIRM_SUFFIX: 'Team Dashboard?',
  DELETE_DASHBOARD_WARNING:
    'This action will permanently delete the dashboard. It will no longer be visible to any DevLens users and',
  DELETE_DASHBOARD_CANNOT_UNDO: 'cannot be undone',

  // Program Enablement
  ENABLE_PROGRAM_TITLE: 'Insufficient Metrics Data',
  ENABLE_PROGRAM_INSUFFICIENT_DATA_PREFIX:
    'This program has insufficient metrics data (70% of required metrics). This will make the program visible to all users on the SE Metrics page.',
  ENABLE_PROGRAM_INSUFFICIENT_DATA_SUFFIX: (name: string) =>
    `Are you sure you want to enable ${name} on the SE Metrics page?`,
} as const

// ===================== Table Configuration =====================
export const TABLE_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MIN_ROW_HEIGHT: 40, // in pixels

  // Column sizes for ManageSEMetricsKPI
  SE_METRICS_COLUMN_SIZES: {
    PROGRAM_NAME: 900, // 90% width
    ACTION: 100, // 10% width
  },
} as const

// ===================== Table Headers & Descriptions =====================
export const TABLE_HEADERS = {
  MANAGE_DASHBOARDS: 'Manage Dashboards',
  MANAGE_DASHBOARDS_DESC: 'You can edit or remove dashboards from here.',
  MANAGE_SE_METRICS: 'Manage SE Metrics Programs',
  MANAGE_SE_METRICS_DESC: 'You can manage view of executive metrics page according to a program.',
} as const

// ===================== Column Definitions =====================
export const COLUMN_KEYS = {
  NAME: 'name',
  ACTION: 'action',
} as const

export const COLUMN_HEADERS = {
  PROGRAM_NAME: 'Program Name',
  TEAM_DASHBOARD_NAME: 'Team Dashboard Name',
  ACTION: 'Action',
} as const

// ===================== Button Labels =====================
export const BUTTON_LABELS = {
  EDIT: 'Edit',
  REMOVE: 'Remove',
  ENABLE: 'Enable',
  CANCEL: 'Cancel',
} as const

// ===================== Search Placeholders =====================
export const SEARCH_PLACEHOLDERS = {
  DASHBOARDS: 'Search dashboards...',
  PROGRAMS: 'Search programs...',
} as const

// ===================== SE Metrics Configuration =====================
export const SE_METRICS_CONFIG = {
  DATA_SUFFICIENCY_THRESHOLD: 70, // Percentage of required metrics
  REQUIRED_KPI_KEYS: [
    'APP_RESPONSE_TIME',
    'UNIT_TEST_COVERAGE',
    'TEST_AUTOMATION_TITLE',
    'REUSABILITY_TITLE',
    'DF_AND_CFR',
  ],
} as const

// ===================== API Configuration =====================
export const API_CONFIG = {
  REFETCH_DELAY: 100, // milliseconds
} as const

// ===================== Global SE Metrics KPI =====================

// Field control types rendered in the edit side panel.
export const GLOBAL_KPI_FIELD_TYPE = {
  PERCENT: 'percent',
  STEPPER: 'stepper',
} as const

export type GlobalKpiFieldType = (typeof GLOBAL_KPI_FIELD_TYPE)[keyof typeof GLOBAL_KPI_FIELD_TYPE]

export interface GlobalKpiDisplayField {
  key: BenchmarkMetricKey
  label: string
  unit?: string
}

export interface GlobalKpiOrderedField {
  key: BenchmarkMetricKey
  label: string
  type: GlobalKpiFieldType
  unit?: string
}

// Left column of the read-only KPI display grid.
export const GLOBAL_KPI_LEFT_DISPLAY: GlobalKpiDisplayField[] = [
  {key: 'aiAdoption', label: 'AI Adoption', unit: '%'},
  {key: 'functionalTestAutomation', label: 'Functional Test Automation', unit: '%'},
  {key: 'overallAutomationCoverage', label: 'Overall Automation Coverage', unit: '%'},
  {key: 'escapeDefects', label: 'Escape Defects', unit: '%'},
  {key: 'unitTestCoverage', label: 'Unit Test Coverage', unit: '%'},
  {key: 'systemAvailability', label: 'System Availability', unit: '%'},
  {key: 'changeFailureRate', label: 'Change Failure Rate', unit: '%'},
  {key: 'buildSuccessRate', label: 'Build Success Rate', unit: '%'},
  {key: 'gtAdoptionRate', label: 'GT Adoption Rate', unit: '%'},
  {key: 'prCycleTime', label: 'PR Cycle Time', unit: 'hrs'},
]

// Right column of the read-only KPI display grid.
export const GLOBAL_KPI_RIGHT_DISPLAY: {key: BenchmarkMetricKey; label: string; unit?: string}[] = [
  {key: 'deploymentFrequency', label: 'Deployment Frequency'},
  {key: 'appResponseTime', label: 'App Response Time', unit: 'ms'},
  {key: 'leadTimeToChange', label: 'Lead Time to Change', unit: 'days'},
  {key: 'meanTimeToResolve', label: 'Mean Time To Resolve', unit: 'hrs'},
  {key: 'commits', label: 'Commits'},
  {key: 'pulls', label: 'Pulls'},
  {key: 'prThroughput', label: 'PR Throughput'},
  {key: 'totalVulnerability', label: 'Total Vulnerability'},
  {key: 'appCrashRate', label: 'App Crash Rate', unit: '%'},
]

// Deduplicated ordered list for the single-column edit side panel (top-to-bottom interleave).
export const GLOBAL_KPI_ORDERED_FIELDS: GlobalKpiOrderedField[] = [
  {key: 'aiAdoption', label: 'AI Adoption', type: 'percent'},
  {key: 'deploymentFrequency', label: 'Deployment Frequency', type: 'stepper'},
  {key: 'functionalTestAutomation', label: 'Functional Test Automation', type: 'percent'},
  {key: 'overallAutomationCoverage', label: 'Overall Automation Coverage', type: 'percent'},
  {key: 'appResponseTime', label: 'App Response Time', type: 'percent', unit: 'ms'},
  {key: 'escapeDefects', label: 'Escape Defects', type: 'percent'},
  {key: 'leadTimeToChange', label: 'Lead Time to Change', type: 'percent', unit: 'days'},
  {key: 'unitTestCoverage', label: 'Unit Test Coverage', type: 'percent'},
  {key: 'meanTimeToResolve', label: 'Mean Time To Resolve', type: 'percent', unit: 'hrs'},
  {key: 'systemAvailability', label: 'System Availability', type: 'percent'},
  {key: 'commits', label: 'Commits', type: 'stepper'},
  {key: 'changeFailureRate', label: 'Change Failure Rate', type: 'percent'},
  {key: 'pulls', label: 'Pulls', type: 'stepper'},
  {key: 'buildSuccessRate', label: 'Build Success Rate', type: 'percent'},
  {key: 'prThroughput', label: 'PR Throughput', type: 'stepper'},
  {key: 'gtAdoptionRate', label: 'GT Adoption Rate', type: 'percent'},
  {key: 'totalVulnerability', label: 'Total Vulnerability', type: 'stepper'},
  {key: 'prCycleTime', label: 'PR Cycle Time', type: 'percent', unit: 'hrs'},
  {key: 'appCrashRate', label: 'App Crash Rate', type: 'percent'},
]

// Range validation for `%` fields in the edit side panel.
export const GLOBAL_KPI_PERCENT_MIN = 0
export const GLOBAL_KPI_PERCENT_MAX = 100
export const GLOBAL_KPI_PERCENT_RANGE_MESSAGE = `Value must be between ${GLOBAL_KPI_PERCENT_MIN} and ${GLOBAL_KPI_PERCENT_MAX}%.`

// Allow a number with at most two decimal places (or a partial entry while typing).
export const TWO_DECIMAL_PATTERN = /^\d*\.?\d{0,2}$/
